import { DEFAULT_LANG, isLang, type Lang } from './languages'

export interface ResolveLanguageInput {
  /** i18nEnabled() at the call site. Off always resolves to English. */
  enabled: boolean
  /** The persisted switcher choice, raw from storage (may be junk or null). */
  stored: string | null | undefined
  /** navigator.languages, most preferred first. */
  navigatorLanguages: readonly string[] | null | undefined
}

/**
 * Which interface language to use. Pure: the caller reads storage and the
 * navigator, so this can be tested without a browser and the flag-off path
 * provably reads nothing.
 *
 * Order: flag off gives `en`; then a valid stored choice wins (including an
 * explicit `en`); then the navigator list, most preferred first, where the
 * first entry whose primary subtag we support decides (so `es-MX`, `es-419`,
 * `pt-BR` and `pt-PT` all count); otherwise `en`.
 *
 * An unsupported entry (`fr-FR`) is skipped, but an English one DECIDES: a
 * browser listing `en-US, es` prefers English and gets English, the way
 * Accept-Language negotiation reads the same list. Only a browser that ranks
 * another supported language above English is switched.
 */
export function resolveLanguage({ enabled, stored, navigatorLanguages }: ResolveLanguageInput): Lang {
  if (!enabled) return DEFAULT_LANG
  if (isLang(stored)) return stored
  for (const tag of navigatorLanguages ?? []) {
    if (typeof tag !== 'string') continue
    const primary = tag.trim().toLowerCase().split(/[-_]/)[0]
    if (isLang(primary)) return primary
  }
  return DEFAULT_LANG
}
